import {
    AdminPanelSettings,
    ArrowBack,
    ArrowForward,
    Difference,
    Settings,
    Sync,
    ViewModule,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useContent } from 'contexts/Content/Content';
import { useGame } from 'contexts/Game/Game';
import { useSettings } from 'contexts/Settings/Settings';
import { transformValue } from 'helpers';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import * as setup from 'setup';
import { useResizeObserver } from 'use-resize-observer';

const regenerateLessons = ['1.6', '2.9', '2.14', '2.15', '3.2', '3.4', '3.13', '4.10', '4.20', '5.1', '5.8', '5.9'];

export default function SettingsControls() {
    const [open, setOpen] = useState(false);
    const content = useContent();
    const game = useGame();
    const router = useRouter();
    const settings = useSettings();
    const containerObserver = useResizeObserver<HTMLDivElement>();

    const debugs = useMemo<SettingsControlsDebug[]>(() => {
        return Object.keys(settings)
            .filter(name => name.startsWith('debug'))
            .map(name => ({
                label: name.replace('debug', ''),
                name,
                reloader: settings.reloaders.includes(name),
            }));
    }, [settings]);

    const lesson = useMemo(() => {
        return /lessons\/(.*)/.exec(router.asPath)?.[1] || null;
    }, [router.asPath]);

    const prevLesson = useMemo(() => {
        const lessonIndex = lesson ? setup.lessonKeys.indexOf(lesson) : -1;
        return setup.lessonKeys[lessonIndex - 1] || null;
    }, [lesson]);

    const nextLesson = useMemo(() => {
        const lessonIndex = lesson ? setup.lessonKeys.indexOf(lesson) : -1;
        return setup.lessonKeys[lessonIndex + 1] || null;
    }, [lesson]);

    const padding = useMemo(() => {
        return (open && containerObserver.height) || 0;
    }, [containerObserver.height, open]);

    function handleChange(e: any) {
        let { name, type, value } = e.target;

        if (type === 'checkbox') {
            value = e.target.checked;
        } else if (type === 'number') {
            value = value ? parseFloat(value) : 1;
        }

        if (name === 'prediction') {
            game.updatePredictions({
                initialMonthly: value,
            });
        } else {
            settings.set(name, value);
        }
    }

    function handleClearHistory() {
        if (settings.debugProgress) {
            window.location.reload();
        } else {
            window.location.href = process.env.NEXT_CONFIG_BASE_PATH || '/';
        }
    }

    function handleLessonChange(e: any) {
        router.push(`/lessons/${e.target.value}`);
    }

    const steps = useMemo(() => {
        return game.steps.map((step, index) => {
            const { decimals, multiplier, operator } = setup.game.steps[index].conversion;
            const base = game.results.bases[index];

            return {
                baseConversion: operator ? `${transformValue(base.conversion, multiplier, decimals)}${operator}` : '',
                baseMax: base.max.toLocaleString(),
                baseMin: base.min.toLocaleString(),
                baseStep: base.step.toLocaleString(),
                conversionMax: operator
                    ? `${transformValue(step.conversion.max, multiplier, decimals)}${operator}`
                    : '',
                conversionMin: operator
                    ? `${transformValue(step.conversion.min, multiplier, decimals)}${operator}`
                    : '',
                max: step.max.toLocaleString(),
                min: step.min.toLocaleString(),
            };
        });
    }, [game.results.bases, game.steps]);

    return (
        <>
            <Box
                sx={{
                    bottom: 0,
                    color: 'grey',
                    left: 0,
                    position: 'fixed',
                    zIndex: 1200
                }}>
                <IconButton
                    aria-label={open ? 'Close settings' : 'Open settings'}
                    color="inherit"
                    data-testid="settings-toggle"
                    onClick={() => setOpen(!open)}
                >
                    <Settings aria-hidden />
                </IconButton>
            </Box>
            {open && (
                <>
                    <Box
                        sx={{
                            height: padding,
                            marginTop: 5
                        }} />
                    <Box
                        ref={containerObserver.ref}
                        sx={{
                            bottom: -16,
                            left: 60,
                            position: 'fixed',
                            right: 60,
                            zIndex: 1200
                        }}>
                        <Paper aria-label="Settings panel" data-testid="settings-panel" elevation={8}>
                            <Box
                                sx={{
                                    paddingBottom: 3,
                                    paddingX: 2,
                                    paddingTop: 1
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 2
                                    }}>
                                    <Box sx={{
                                        flex: 3
                                    }}>
                                        <Typography sx={{
                                            marginBottom: 0
                                        }}>Lessons</Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2
                                            }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                <IconButton
                                                    size="small"
                                                    title="Clear history"
                                                    onClick={handleClearHistory}
                                                >
                                                    <Sync />
                                                </IconButton>
                                                <IconButton
                                                    component={NextLink}
                                                    disabled={!prevLesson}
                                                    href={`/lessons/${prevLesson}`}
                                                    size="small"
                                                    title="Previous"
                                                >
                                                    <ArrowBack />
                                                </IconButton>
                                                <IconButton
                                                    component={NextLink}
                                                    disabled={!nextLesson}
                                                    href={`/lessons/${nextLesson}`}
                                                    size="small"
                                                    title="Next"
                                                >
                                                    <ArrowForward />
                                                </IconButton>
                                                <IconButton
                                                    component={NextLink}
                                                    href="/admin/lessons"
                                                    size="small"
                                                    title="Lessons overview"
                                                >
                                                    <ViewModule />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    title="Reload content"
                                                    onClick={content.reload}
                                                >
                                                    <Difference />
                                                </IconButton>
                                                <IconButton
                                                    component={NextLink}
                                                    href="/admin"
                                                    size="small"
                                                    title="Admin panel"
                                                >
                                                    <AdminPanelSettings />
                                                </IconButton>
                                            </Box>
                                            <TextField
                                                fullWidth
                                                label="Lesson"
                                                name="lesson"
                                                select
                                                value={lesson || ''}
                                                onChange={handleLessonChange}
                                            >
                                                {setup.lessonKeys.map((lesson, index) => (
                                                    <MenuItem
                                                        divider={setup.lessonKeys[index + 1]?.length === 1}
                                                        key={index}
                                                        sx={{ fontWeight: lesson.length === 1 ? 600 : 400 }}
                                                        value={lesson}
                                                    >
                                                        {lesson}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Divider orientation="vertical" />
                                    </Box>

                                    <Box sx={{
                                        flex: 2
                                    }}>
                                        <Typography
                                            sx={{
                                                marginBottom: 1
                                            }}>
                                            Game
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2
                                            }}>
                                            <TextField
                                                color="info"
                                                label="Volume"
                                                name="volume"
                                                type="number"
                                                value={settings.volume}
                                                onChange={handleChange}
                                                slotProps={{
                                                    htmlInput: { max: 1, min: 0.01, step: 0.01 }
                                                }}
                                            />
                                            <TextField
                                                color="info"
                                                label="Initial prediction"
                                                name="prediction"
                                                type="number"
                                                value={game.predictions.initialMonthly}
                                                onChange={handleChange}
                                                slotProps={{
                                                    htmlInput: {
                                                        max: setup.game.prediction.max,
                                                        min: setup.game.prediction.min,
                                                        step: 100,
                                                    }
                                                }}
                                            />
                                            <Box
                                                title={`Regenerate values for current lesson. Only available for ${regenerateLessons.join(
                                                    ', '
                                                )}.`}
                                            >
                                                <Button
                                                    color="info"
                                                    disabled={!regenerateLessons.includes(lesson || '')}
                                                    fullWidth
                                                    startIcon={<Sync />}
                                                    onClick={() => game.regenerate()}
                                                >
                                                    Current
                                                </Button>
                                            </Box>
                                            <Button
                                                color="info"
                                                startIcon={<Sync />}
                                                title="Regenerate all values up to and including current lesson"
                                                onClick={() => game.regenerate(true)}
                                            >
                                                All
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Divider orientation="vertical" />
                                    </Box>

                                    <Box sx={{
                                        flex: 4
                                    }}>
                                        <Typography>Steps</Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="right" variant="head">
                                                        Min
                                                    </TableCell>
                                                    <TableCell align="right" variant="head">
                                                        Max
                                                    </TableCell>
                                                    <TableCell align="right" variant="head">
                                                        Conversion Min
                                                    </TableCell>
                                                    <TableCell align="right" variant="head">
                                                        Conversion Max
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {steps.map((step, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell align="right">{step.min}</TableCell>
                                                        <TableCell align="right">{step.max}</TableCell>
                                                        <TableCell align="right">{step.conversionMin}</TableCell>
                                                        <TableCell align="right">{step.conversionMax}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>

                                    <Box>
                                        <Divider orientation="vertical" />
                                    </Box>

                                    <Box sx={{
                                        flex: 4
                                    }}>
                                        <Typography>Results</Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="right" variant="head">
                                                        Base Min
                                                    </TableCell>
                                                    <TableCell align="right" variant="head">
                                                        Base Max
                                                    </TableCell>
                                                    <TableCell align="right" variant="head">
                                                        Base Value
                                                    </TableCell>
                                                    <TableCell align="right" variant="head">
                                                        Base Conversion
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {steps.map((step, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell align="right">{step.baseMin}</TableCell>
                                                        <TableCell align="right">{step.baseMax}</TableCell>
                                                        <TableCell align="right">{step.baseStep}</TableCell>
                                                        <TableCell align="right">{step.baseConversion}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>

                                    <Box>
                                        <Divider orientation="vertical" />
                                    </Box>

                                    <Box sx={{
                                        flex: 4
                                    }}>
                                        <Typography>Debugs</Typography>
                                        <Box
                                            sx={{
                                                display: 'grid',

                                                gridTemplateColumns: {
                                                    sm: '1fr',
                                                    md: '1fr 1fr',
                                                    lg: '1fr 1fr 1fr',
                                                    xl: '1fr 1fr 1fr 1fr',
                                                }
                                            }}>
                                            {debugs.map((debug, index) => (
                                                <Box key={index}>
                                                    <FormControlLabel
                                                        color="info"
                                                        control={
                                                            <Checkbox
                                                                checked={settings[debug.name]}
                                                                color="info"
                                                                name={debug.name}
                                                                size="small"
                                                                sx={{ padding: 0.5 }}
                                                                value=""
                                                                onChange={handleChange}
                                                            />
                                                        }
                                                        label={`${debug.label}${debug.reloader ? '*' : ''}`}
                                                        slotProps={{
                                                            typography: {
                                                                variant: 'montserrat414',
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                </>
            )}
        </>
    );
}
